import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import { formatVnd, GAME_BY_ID } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { MoneyInput } from '../components/MoneyInput';

// Luat hu (config `hu` tren he A, backend/lib/cau_hinh_hu.js): tab "Luat hu" cua trang Hu, cung quyen
// jackpots.manage voi lenh JACKPOT (nguoi tao va nguoi duyet). He A tra danh sach hu CMS dat duoc,
// khoang tung truong, gia tri dang ap dung va mac dinh; o de trong = mac dinh (file config cua game).
// Gia hu va moc hu (tien sau khi no) doi o tab "Hu dang chay" bang lenh JACKPOT, khong o day.
type He = 'heA' | 'heB' | 'ban';
type Range = 'bool' | [number, number, boolean];
type Val = number | boolean;
type Pot = { truong: Record<string, Range>; hieuLuc: Record<string, Val>; macDinh: Record<string, Val> };
type Doc = Partial<Record<He, Record<string, Record<string, Val>>>>;
type Last = { id: string; status: string; createdAt: string; lastError: string | null; chiTiet: string | null };
type Data = {
  noiDung: Doc | null; hieuLuc: Record<He, Record<string, Pot>> | null;
  meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string; createdAt: string }[]; last: Last | null;
};
type Form = Record<string, string>;   // '<he>.<game>.<truong>' -> chuoi nhap ('' = mac dinh)

export const potGameId = (he: string, g: string) => (he === 'heA' ? 'kts_' + g : he === 'ban' ? 'ktrng_' + g : g);
export const potName = (he: string, g: string) => GAME_BY_ID.get(potGameId(he, g))?.name ?? g;
export const FIELD: Record<string, string> = {
  phanTramVaoHu: 'Phần cược góp vào hũ (%)',
  phanTramKhoiTao: 'Tần suất nổ (% cược trả cho tiền khởi tạo)',
  cuocToiThieu: 'Cược Lucky 88 tối thiểu để dự hũ',
  bat: 'Hũ x88',
  tranHu: 'Trần hũ (chạm trần thì bot nổ hũ)',
};
const HELP: Record<string, string> = {
  phanTramKhoiTao: 'Cao hơn thì hũ nổ thường hơn, mỗi lần nổ nhỏ hơn. 0 = không nổ ngẫu nhiên.',
  tranHu: 'Hũ chạm mức này thì một bot nổ cả hũ, hũ về tiền khởi tạo. 0 = không có trần; trần phải lớn hơn tiền khởi tạo.',
};
const MONEY_KEYS = new Set(['cuocToiThieu', 'tranHu']);

const key = (he: He, g: string, k: string) => he + '.' + g + '.' + k;
const show = (r: Range, v: Val | undefined) => (v == null ? '' : r === 'bool' ? (v ? 'Bật' : 'Tắt') : r[2] ? formatVnd(Number(v)) : String(v) + '%');

function toForm(doc: Doc | null): Form {
  const f: Form = {};
  for (const he of ['heA', 'heB', 'ban'] as He[]) {
    for (const [g, o] of Object.entries(doc?.[he] ?? {})) {
      for (const [k, v] of Object.entries(o)) f[key(he, g, k)] = typeof v === 'boolean' ? (v ? 'bat' : 'tat') : String(v);
    }
  }
  return f;
}

// Cung khoang voi he A (kiemCms): loi theo o, hoac tai lieu CMS
function toDoc(f: Form, pots: Data['hieuLuc']): { doc: Doc; errors: Record<string, string> } {
  const doc: Doc = {};
  const errors: Record<string, string> = {};
  for (const he of ['heA', 'heB', 'ban'] as He[]) {
    for (const [g, p] of Object.entries(pots?.[he] ?? {})) {
      const o: Record<string, Val> = {};
      for (const [k, r] of Object.entries(p.truong)) {
        const s = (f[key(he, g, k)] ?? '').trim();
        if (!s) continue;
        if (r === 'bool') { o[k] = s === 'bat'; continue; }
        const n = Number(s.replace(',', '.'));
        if (!(Number.isFinite(n) && n >= r[0] && n <= r[1]) || (r[2] && !Number.isSafeInteger(n))) {
          errors[key(he, g, k)] = r[2] ? 'Số nguyên từ ' + formatVnd(r[0]) + ' đến ' + formatVnd(r[1]) : 'Từ ' + r[0] + '% đến ' + r[1] + '%';
        } else o[k] = n;
      }
      if (Object.keys(o).length) doc[he] = { ...doc[he], [g]: o };
    }
  }
  return { doc, errors };
}

export function describeHu(doc: Doc) {
  const out: string[] = [];
  for (const he of ['ban', 'heA', 'heB'] as He[]) {
    for (const [g, o] of Object.entries(doc[he] ?? {})) {
      const p = Object.entries(o).map(([k, v]) => (FIELD[k] ?? k) + ': ' + (typeof v === 'boolean' ? (v ? 'Bật' : 'Tắt') : MONEY_KEYS.has(k) ? formatVnd(v) : v + '%'));
      out.push(potName(he, g) + ': ' + p.join(', '));
    }
  }
  return out.length ? out : ['Bỏ mọi giá trị CMS: mọi hũ dùng mặc định.'];
}

const decimal = (v: string) => v.replace(',', '.').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 8);
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function Field({ he, g, k, r, pot, f, set, err }: { he: He; g: string; k: string; r: Range; pot: Pot; f: Form; set: (k: string, v: string) => void; err?: string }) {
  const id = key(he, g, k);
  const placeholder = show(r, pot.hieuLuc[k]) + (pot.hieuLuc[k] === pot.macDinh[k] ? ' (mặc định)' : '');
  if (r === 'bool') {
    return (
      <Select id={id} size="sm" labelText={FIELD[k] ?? k} value={f[id] ?? ''} onChange={(e) => set(id, e.target.value)}>
        <SelectItem value="" text={'Mặc định: ' + show(r, pot.macDinh[k])} />
        <SelectItem value="bat" text="Bật" />
        <SelectItem value="tat" text="Tắt" />
      </Select>
    );
  }
  const money = r[2];
  const v = f[id] ?? '';
  return money ? (
    <MoneyInput id={id} size="sm" labelText={FIELD[k] ?? k} placeholder={placeholder} maxDigits={13}
      helperText={HELP[k]} value={v} onChange={(d) => set(id, d)} invalid={!!err} invalidText={err} />
  ) : (
    <TextInput id={id} size="sm" labelText={FIELD[k] ?? k} inputMode="decimal" placeholder={placeholder}
      helperText={HELP[k]} value={v} onChange={(e) => set(id, decimal(e.target.value))} invalid={!!err} invalidText={err} />
  );
}

const GROUPS: { he: He; title: string; note: string; only?: (g: string, p: Pot) => boolean }[] = [
  { he: 'ban', title: 'Hũ bàn', note: 'Baccarat: người cược ô Lucky 88 từ mức tối thiểu trở lên được dự hũ; ván ra Lucky 88 thì họ chia cả hũ. Stud Poker: phần cược JACKPOT góp vào hũ Royal Flush. Đổi giữa ván thì áp dụng từ ván sau.' },
  { he: 'heA', title: 'Slot', note: 'Mỗi lượt quay góp một phần cược vào hũ của mức cược đó.' },
  { he: 'heB', title: 'Slot và mini game', note: 'Thần Tài, Kim Cương, Night Club Lady, Mini Poker; Mega Poker chỉ đặt phần góp; ZicZac đặt phần góp và trần hũ.' },
];

function Editor({ data, editable }: { data: Data; editable: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const base = toForm(data.noiDung);
  const [f, setF] = useState<Form>(base);
  const [open, setOpen] = useState(false);
  const [stale, setStale] = useState(false);
  const { doc, errors } = toDoc(f, data.hieuLuc);
  const bad = Object.keys(errors).length > 0;
  const clean = (x: Form) => JSON.stringify(Object.fromEntries(Object.entries(x).filter(([, v]) => v !== '').sort()));
  const dirty = clean(f) !== clean(base);
  const set = (k: string, v: string) => setF((o) => ({ ...o, [k]: v }));
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/jackpots/rules', { noiDung: doc, expect: data.meta.version }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
    onError: (e) => { if (isStale(e)) { setOpen(false); setStale(true); qc.invalidateQueries({ queryKey: ['hu-cau-hinh'] }); } },
  });
  const pots = data.hieuLuc!;
  return (
    <>
      {stale ? <InlineNotification kind="warning" lowContrast title="Cấu hình vừa được người khác lưu"
        subtitle="Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại." onClose={() => setStale(false)} /> : null}
      {GROUPS.map((gr) => (
        <section key={gr.he} className="section">
          <h2>{gr.title}</h2>
          <p className="muted">{gr.note}</p>
          <div className="control-grid">
            {Object.entries(pots[gr.he] ?? {}).map(([g, p]) => (
              <section key={g} className="panel control" data-hu={gr.he + '.' + g}>
                <div className="panel__head">
                  <h3>{potName(gr.he, g)}</h3>
                  {data.noiDung?.[gr.he]?.[g] ? <Tag size="sm" type="blue">CMS đặt</Tag> : <Tag size="sm" type="gray">mặc định</Tag>}
                </div>
                <fieldset className="control__group" disabled={!editable}>
                  {Object.entries(p.truong).map(([k, r]) => (
                    <Field key={k} he={gr.he} g={g} k={k} r={r} pot={p} f={f} set={set} err={errors[key(gr.he, g, k)]} />
                  ))}
                </fieldset>
              </section>
            ))}
          </div>
        </section>
      ))}
      {editable ? (
        <div className="row section">
          <Button kind="ghost" disabled={!dirty} onClick={() => setF(base)}>Bỏ thay đổi</Button>
          <Button disabled={!dirty || bad || data.pending.length > 0} onClick={() => setOpen(true)}>Lưu, áp dụng ngay</Button>
        </div>
      ) : null}
      <Modal open={open} modalHeading="Đổi luật hũ" primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <p>Thay đổi này đổi số tiền trả cho người chơi. Hệ A áp dụng ngay, không chờ người duyệt. Ô để trống dùng mặc định.</p>
          <ul className="list">{describeHu(doc).map((x) => <li key={x}>{x}</li>)}</ul>
          <InlineError error={m.error} />
        </div>
      </Modal>
    </>
  );
}

// Tab "Luat hu" cua trang Hu
export function JackpotRules() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['hu-cau-hinh'], queryFn: () => api.get<Data>('/api/admin/jackpots/rules') });
  if (q.isLoading) return <BlockSkeleton lines={10} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { meta, pending, last, hieuLuc } = q.data;
  return (
    <div className="stack">
      <p className="muted">Phần cược góp vào hũ, tần suất nổ và luật hũ x88 của Baccarat. Ô để trống thì game dùng mặc định. Đổi luật áp dụng ngay khi lưu, không chờ người duyệt, từ lượt quay, tay bài hay ván sau.</p>
      <div className="row health">
        <span>Luật hũ CMS: {meta.source === 'default' ? <span className="muted">chưa đặt, game dùng mặc định</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {last?.status === 'FAILED' ? (
        <InlineNotification kind="error" lowContrast hideCloseButton title="Lệnh gần nhất bị hệ A từ chối"
          subtitle={[(last.lastError && HE_A_ERRORS[last.lastError]) || last.lastError, last.chiTiet].filter(Boolean).join(': ')} />
      ) : null}
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong, hoặc duyệt / từ chối lệnh còn chờ duyệt từ trước, rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      {hieuLuc ? <Editor key={meta.version} data={q.data} editable={can('jackpots.manage')} />
        : <ErrorState error={new Error('Hệ A chưa trả danh sách hũ. Thử lại sau.')} onRetry={() => q.refetch()} />}
    </div>
  );
}
